<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIntersectionObserver, refDebounced } from '@vueuse/core'
import { deepGet } from 'ts-deep-utils'
import { useInfiniteQuery } from '@/composables/api.ts'
import { useCreditorSettings } from '@/composables/useCreditorSettings.ts'
import { formatDate } from '@/utils/format.ts'
import type { components } from '@/types/_api'

type StatisticsCreditorDay = components['schemas']['Statistics-CreditorDay']

interface EventItem {
  creditorName: string
  date: string
  key: string
  value: number
}

const { t } = useI18n()
const { creditorWhereClause, hasSelection } = useCreditorSettings()

const animationCompleted = ref(false)

// Coalesce rapid filter toggles into one refetch.
const debouncedWhereClause = refDebounced(creditorWhereClause, 200)

const eventsQuery = useInfiniteQuery(
  '/statistics--creditor-days',
  computed(() => ({
    query: {
      sort: '-date',
      where: debouncedWhereClause.value ?? {},
    },
  })),
  { enabled: hasSelection }
)

const loadMoreRef = useTemplateRef('loadMoreRef')

useIntersectionObserver(
  loadMoreRef,
  ([entry]) => {
    if (entry?.isIntersecting && eventsQuery.hasNextPage.value && !eventsQuery.isFetchingNextPage.value) {
      void eventsQuery.fetchNextPage()
    }
  },
  { rootMargin: '100px 0px 0px 0px' }
)

function processItem(item: StatisticsCreditorDay): EventItem[] {
  const result: EventItem[] = []
  const dottedKeyPaths = ['other.capital_fully_paid']
  for (const key of Object.keys(item.start_actions)) {
    dottedKeyPaths.push(`start_actions.${key}`)
  }
  for (const dottedKeyPath of dottedKeyPaths) {
    const value = deepGet(item as Record<string, unknown>, dottedKeyPath)
    if (typeof value === 'number' && value > 0) {
      result.push({
        creditorName: item.creditor_name ?? '',
        date: formatDate(item.date),
        key: dottedKeyPath.replaceAll('.', '_'),
        value,
      })
    }
  }
  return result
}

const processedItems = computed<EventItem[]>(() => {
  if (eventsQuery.data.value?.pages == null) return []
  return eventsQuery.data.value.pages.flatMap((page) => (page._items ?? []).flatMap((item) => processItem(item)))
})

watch(
  () => processedItems.value.length > 0,
  (hasItems) => {
    if (hasItems) {
      animationCompleted.value = true
    }
  }
)

function getEventText(item: EventItem): string {
  const suffix = item.value === 1 ? '' : '_multiple'
  const key = `common.start_page_events_${item.key}${suffix}`
  return t(key, [item.value])
}
</script>

<template>
  <v-card
    class="d-flex flex-column h-100"
    variant="outlined"
  >
    <v-card-title class="px-6 pt-6 pb-4">
      <h2 class="text-body-medium font-weight-bold">{{ t('common.latest_events') }}</h2>
    </v-card-title>
    <section
      v-if="processedItems.length === 0"
      class="flex-grow-1 d-flex align-center justify-center"
    >
      <v-progress-circular
        v-if="eventsQuery.isLoading.value"
        color="brand-primary"
        indeterminate
      />
      <v-fade-transition>
        <p
          v-show="!eventsQuery.isLoading.value"
          class="text-body-medium font-weight-medium text-medium-emphasis w-50"
        >
          {{ t('common.latest_events_empty_state') }}
        </p>
      </v-fade-transition>
    </section>
    <section
      v-else
      class="flex-grow-1"
      :class="{ 'overflow-auto': animationCompleted, 'overflow-hidden': !animationCompleted }"
    >
      <v-scroll-y-transition group>
        <article
          v-for="(item, index) in processedItems"
          :key="`${item.date}-${item.key}-${index}`"
          class="px-6"
        >
          <div class="py-4 px-2">
            <h4 class="mb-2 text-title-small font-weight-medium text-truncate">{{ getEventText(item) }}</h4>
            <div class="text-body-small text-medium-emphasis d-flex align-center ga-2">
              <span class="text-uppercase font-weight-medium">{{ item.creditorName }}</span>
              <span
                class="font-weight-medium"
                aria-hidden="true"
              >
                |
              </span>
              <span>{{ formatDate(item.date) }}</span>
            </div>
          </div>
          <v-divider />
        </article>
      </v-scroll-y-transition>

      <div
        v-if="eventsQuery.hasNextPage.value"
        ref="loadMoreRef"
        class="px-6 pb-4 d-flex justify-center mt-4"
      >
        <v-btn
          :loading="eventsQuery.isFetchingNextPage.value"
          :disabled="eventsQuery.isFetchingNextPage.value"
          variant="outlined"
          @click="void eventsQuery.fetchNextPage()"
        >
          {{ t('common.show_more') }}
        </v-btn>
      </div>
    </section>
  </v-card>
</template>
